// Shared display helpers for the Club Planner pages. Display only: day labels, day types and
// kickoff times all come from the server (services/planner/) — nothing here derives them.

export const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DOW_SUN0 = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MON_SHORT = MONTHS.map(m => m.slice(0, 3));

const pad = n => String(n).padStart(2, '0');

export function localToday() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return dt.toISOString().slice(0, 10);
}

export function mondayOf(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay() || 7;
  return addDays(dateStr, 1 - dow);
}

// ISO week navigation (display only — the server decides which days and blocks belong to a week).
export function isoWeekOf(dateStr) {
  const [y, m, day] = dateStr.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1, day));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const isoYear = d.getUTCFullYear();
  return { isoYear, isoWeek: Math.ceil(((d - Date.UTC(isoYear, 0, 1)) / 86400000 + 1) / 7) };
}
export function isoWeekMonday(isoYear, isoWeek) {
  const jan4 = `${isoYear}-01-04`;
  return addDays(mondayOf(jan4), (isoWeek - 1) * 7);
}
export const weekParam = (isoYear, isoWeek) => `${isoYear}-W${String(isoWeek).padStart(2, '0')}`;
export function parseWeekParam(s) {
  const m = /^(\d{4})-W(\d{1,2})$/.exec(s || '');
  return m ? { isoYear: Number(m[1]), isoWeek: Number(m[2]) } : null;
}
export function shiftWeek(isoYear, isoWeek, delta) {
  return isoWeekOf(addDays(isoWeekMonday(isoYear, isoWeek), 7 * delta));
}

export const toMin = t => {
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};
export const fromMin = m => `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;

export function monthBounds(month) { // 'YYYY-MM'
  const [y, m] = month.split('-').map(Number);
  const first = `${y}-${pad(m)}-01`;
  const last = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
  return { first, last, gridFrom: mondayOf(first), gridTo: addDays(mondayOf(last), 6) };
}

export function shiftMonth(month, delta) {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`;
}

export function monthTitle(month) {
  const [y, m] = month.split('-').map(Number);
  return `${MONTHS[m - 1]} ${y}`;
}

export function dayNum(dateStr) { return Number(dateStr.slice(8, 10)); }

export function fmtDayShort(dateStr) { // "Sun 18 Oct"
  const [y, m, d] = dateStr.split('-').map(Number);
  return `${DOW_SUN0[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]} ${d} ${MON_SHORT[m - 1]}`;
}

export function fmtRange(fromStr, toStr) { // "12 – 18 Oct" / "28 Sep – 4 Oct"
  const [, m1, d1] = fromStr.split('-').map(Number);
  const [, m2, d2] = toStr.split('-').map(Number);
  return m1 === m2 ? `${d1} – ${d2} ${MON_SHORT[m2 - 1]}` : `${d1} ${MON_SHORT[m1 - 1]} – ${d2} ${MON_SHORT[m2 - 1]}`;
}

export function fmtSyncTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return sameDay ? `today ${time}` : `${d.getDate()} ${MON_SHORT[d.getMonth()]} ${time}`;
}

export function fixtureLine(f) {
  const comp = f.competition && f.competition !== 'League' ? ` · ${f.competition}` : '';
  return `${f.opponent} (${f.home_away})${comp}`;
}

// Planner colour tokens, night (app default) and day theme. Game days are the strongest
// contrast in either theme; off days are muted.
export const PLANNER_CSS = `
.planner-root {
  --pl-game-bg: #e8edf4; --pl-game-fg: #0b1220; --pl-game-sub: #334155;
  --pl-off-bg: #0b1120; --pl-off-fg: #64748b;
  --pl-train-bg: var(--bg-card); --pl-train-fg: var(--text-primary);
  --pl-pill-bg: rgba(148,163,184,0.16); --pl-pill-fg: var(--text-secondary);
  --pl-today: #3b82f6; --pl-accent: #3b82f6; --pl-warn-bg: rgba(245,158,11,0.10); --pl-warn-border: #b45309;
  --pl-ok: #10b981; --pl-err: #ef4444;
}
[data-theme="day"] .planner-root {
  --pl-game-bg: #15171a; --pl-game-fg: #ffffff; --pl-game-sub: #cbd5e1;
  --pl-off-bg: #e5e7eb; --pl-off-fg: #6b7280;
  --pl-pill-bg: #eef1f5; --pl-pill-fg: #334155;
  --pl-warn-bg: #fff7e6; --pl-warn-border: #d97706;
}
.planner-root .pl-btn { padding: 7px 12px; border-radius: 8px; border: 1px solid var(--border-strong); background: var(--bg-card); color: var(--text-secondary); font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; min-height: 34px; }
.planner-root .pl-btn:hover:not(:disabled) { color: var(--text-primary); border-color: var(--text-muted); }
.planner-root .pl-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.planner-root .pl-btn--primary { background: var(--pl-accent); border-color: var(--pl-accent); color: #fff; }
.planner-root .pl-btn--primary:hover:not(:disabled) { color: #fff; filter: brightness(1.08); }
.planner-root .pl-btn--active { background: rgba(59,130,246,0.14); border-color: var(--pl-accent); color: var(--text-primary); }
.planner-root .pl-pill { font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 11px; padding: 1px 6px; border-radius: 4px; background: var(--pl-pill-bg); color: var(--pl-pill-fg); white-space: nowrap; }
.planner-root .pl-input { padding: 7px 9px; border-radius: 8px; border: 1px solid var(--border-strong); background: var(--bg-app); color: var(--text-primary); font: inherit; font-size: 13px; }
.planner-root .pl-label { font-size: 12px; color: var(--text-secondary); display: flex; flex-direction: column; gap: 4px; }
.planner-root .pl-cell { position: relative; min-height: 84px; padding: 6px 7px; border-radius: 8px; border: 1px solid var(--border); background: var(--pl-train-bg); color: var(--pl-train-fg); display: flex; flex-direction: column; gap: 3px; text-align: left; font: inherit; cursor: pointer; user-select: none; }
.planner-root .pl-cell--off { background: var(--pl-off-bg); color: var(--pl-off-fg); }
.planner-root .pl-cell--game { background: var(--pl-game-bg); color: var(--pl-game-fg); border-color: transparent; }
.planner-root .pl-cell--game .pl-pill { background: rgba(127,127,127,0.25); color: inherit; }
.planner-root .pl-cell--outside { opacity: 0.42; }
.planner-root .pl-cell--today { box-shadow: 0 0 0 2px var(--pl-today); }
.planner-root .pl-cell--preview { outline: 2px dashed var(--pl-accent); outline-offset: -3px; }
.planner-root .pl-cell:focus-visible { outline: 2px solid var(--pl-accent); outline-offset: 1px; }
.planner-root .pl-tag { margin-top: auto; align-self: flex-start; font-size: 10px; font-weight: 700; letter-spacing: 0.06em; line-height: 1; padding: 3px 6px; border-radius: 4px; }
.planner-root .pl-tag--tr { background: #1f6f43; color: #ffffff; }
.planner-root .pl-tag--off { border: 1px solid currentColor; color: var(--pl-off-fg); }
`;
